import classNames from 'classnames';
import { HTMLAttributes, ReactNode, RefObject, forwardRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { setPopupLocksScroll, useOpenedPopupsValue, useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import { lockBodyScroll, unlockBodyScroll } from '@/shared/lib/utils/dom';

export interface Props extends HTMLAttributes<HTMLElement> {
    containerRef?: RefObject<HTMLDivElement>;
    /**
     * Уникальное имя попапа
     */
    name: string;
    /**
     * Рендерить ли оверлей
     */
    overlay?: boolean;
    onOpen?: () => void;
    onOpenComplete?: () => void;
    onClose?: () => void;
    onCloseComplete?: () => void;
    wrapperSlot?: ReactNode;
    lockScroll?: boolean;
}

const root = typeof window !== 'undefined' ? document.querySelector('#modal-root') : null;

const Popup = forwardRef<HTMLDivElement, Props>(
    ({ wrapperSlot, children, containerRef, overlay = false, lockScroll = true, name, ...props }, ref) => {
        const openedPopups = useOpenedPopupsValue();
        const { closePopup } = useSetOpenedPopupsState();
        const target = openedPopups.includes(name);

        useEffect(() => {
            const onKeydown = (event: KeyboardEvent) => {
                if (event.code === 'Escape') {
                    closePopup(name);
                    unlockBodyScroll();
                }
            };

            document.addEventListener('keydown', onKeydown);

            return () => document.removeEventListener('keydown', onKeydown);
        }, [closePopup, name]);

        useEffect(() => {
            return () => {
                closePopup(name);
                unlockBodyScroll();
            };
        }, [closePopup, name]);

        useEffect(() => {
            setPopupLocksScroll(name, lockScroll);

            if (!lockScroll) {
                if (!target) {
                    unlockBodyScroll();
                }

                return () => {
                    setPopupLocksScroll(name, true);
                };
            }

            if (target) {
                lockBodyScroll();
            } else {
                unlockBodyScroll();
            }

            return () => {
                setPopupLocksScroll(name, true);
                if (target) {
                    unlockBodyScroll();
                }
            };
        }, [target, lockScroll, name]);

        const Component = (
            <div
                {...props}
                ref={ref}
                className={classNames(props.className, 'popup', { 'popup--opened': openedPopups.includes(name) })}
            >
                {overlay && <div className="popup-overlay" onClick={() => closePopup(name)}></div>}
                {wrapperSlot}
                <div ref={containerRef} className="popup-container">
                    {children}
                </div>
            </div>
        );

        return root ? createPortal(Component, root) : Component;
    },
);

Popup.displayName = 'Popup';

export default Popup;
